import React, { useState, useEffect } from 'react'
import Header from '../components/Header'
import Toast from '../components/Toast'
import { staffAPI, stationAPI, scoreAPI, teamsAPI } from '../utils/api'
import ScoreForm from '../components/ScoreForm'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faChalkboardUser,
  faCirclePlus,
  faListCheck,
  faPenToSquare,
  faPeopleGroup,
  faSchool,
  faShieldHalved,
  faTrashCan,
  faUsers,
  faWandMagicSparkles,
  faChartLine,
  faLayerGroup,
  faToggleOn,
} from '@fortawesome/free-solid-svg-icons'

function Admin({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('staff')
  const [staff, setStaff] = useState([])
  const [stations, setStations] = useState([])
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState(null)
  const [editingStaff, setEditingStaff] = useState(null)
  const [editingMode, setEditingMode] = useState('teams')
  const [newStationName, setNewStationName] = useState('')

  const tabConfig = [
    { id: 'staff', label: 'Staff Management', icon: faUsers, description: 'Assign roles, teams, and stations' },
    { id: 'stations', label: 'Stations', icon: faSchool, description: 'Create and remove score stations' },
    { id: 'scores', label: 'Add Score', icon: faChartLine, description: 'Manually add score as admin' },
  ]

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [staffRes, stationsRes, teamsRes] = await Promise.all([
        staffAPI.getMgmt(),
        stationAPI.getAll(),
        teamsAPI.getAll(),
      ])
      setStaff(staffRes.data)
      setStations(stationsRes.data.filter(s => !s.deleted))
      setTeams(teamsRes.data)
    } catch (error) {
      setToast({ message: 'Error loading data', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleCreateStation = async (e) => {
    e.preventDefault()
    if (!newStationName.trim()) return

    try {
      await stationAPI.create(newStationName)
      setNewStationName('')
      loadData()
      setToast({ message: '✓ Station created', type: 'success' })
    } catch (error) {
      setToast({ message: 'Error creating station', type: 'error' })
    }
  }

  const handleDeleteStation = async (stationId) => {
    if (window.confirm('Delete this station?')) {
      try {
        await stationAPI.delete(stationId)
        loadData()
        setToast({ message: '✓ Station deleted', type: 'success' })
      } catch (error) {
        setToast({ message: 'Error deleting station', type: 'error' })
      }
    }
  }

  const handleUpdateRole = async (staffId, newRole) => {
    try {
      await staffAPI.updateRole(staffId, newRole)
      loadData()
      setToast({ message: '✓ Role updated', type: 'success' })
    } catch (error) {
      setToast({ message: 'Error updating role', type: 'error' })
    }
  }

  const handleToggleTeam = async (staffId, teamId) => {
    const staffMember = staff.find(s => s.id === staffId)
    const teams = staffMember.teams.includes(teamId)
      ? staffMember.teams.filter(t => t !== teamId)
      : [...staffMember.teams, teamId]

    try {
      await staffAPI.updateTeams(staffId, teams)
      loadData()
    } catch (error) {
      setToast({ message: 'Error updating teams', type: 'error' })
    }
  }

  const handleToggleStation = async (staffId, stationId) => {
    const staffMember = staff.find(s => s.id === staffId)
    const stations = staffMember.stations.includes(stationId)
      ? staffMember.stations.filter(s => s !== stationId)
      : [...staffMember.stations, stationId]

    try {
      await staffAPI.updateStations(staffId, stations)
      loadData()
    } catch (error) {
      setToast({ message: 'Error updating stations', type: 'error' })
    }
  }

  if (loading) {
    return (
      <div>
        <Header user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-screen">
          <div className="spinner"></div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <Header user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        <section className="rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white p-6 md:p-8 shadow-2xl overflow-hidden relative">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,_rgba(59,130,246,0.5),_transparent_35%),radial-gradient(circle_at_bottom_left,_rgba(236,72,153,0.35),_transparent_30%)]" />
          <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-sm font-semibold mb-4">
                <FontAwesomeIcon icon={faShieldHalved} />
                Admin control center
              </div>
              <h1 className="text-3xl md:text-5xl font-black leading-tight mb-3">
                Manage staff, stations, and score entry from one place.
              </h1>
              <p className="text-slate-200 text-base md:text-lg">
                Use the cards below for the fast path. Staff rows now have explicit team and station managers so assignment is not buried.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 min-w-[260px]">
              <div className="rounded-2xl bg-white/10 p-4 backdrop-blur">
                <p className="text-xs uppercase tracking-wider text-slate-300">Staff</p>
                <p className="text-2xl font-black">{staff.length}</p>
              </div>
              <div className="rounded-2xl bg-white/10 p-4 backdrop-blur">
                <p className="text-xs uppercase tracking-wider text-slate-300">Stations</p>
                <p className="text-2xl font-black">{stations.length}</p>
              </div>
              <div className="rounded-2xl bg-white/10 p-4 backdrop-blur">
                <p className="text-xs uppercase tracking-wider text-slate-300">Teams</p>
                <p className="text-2xl font-black">{teams.length}</p>
              </div>
              <div className="rounded-2xl bg-white/10 p-4 backdrop-blur">
                <p className="text-xs uppercase tracking-wider text-slate-300">Tools</p>
                <p className="text-2xl font-black">3</p>
              </div>
            </div>
          </div>
        </section>

        <section className="grid md:grid-cols-3 gap-4">
          {tabConfig.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`text-left rounded-2xl border p-5 transition shadow-sm hover:shadow-lg ${
                activeTab === tab.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="h-11 w-11 rounded-2xl bg-slate-900 text-white flex items-center justify-center mb-4">
                    <FontAwesomeIcon icon={tab.icon} />
                  </div>
                  <h2 className="text-xl font-black text-slate-900">{tab.label}</h2>
                  <p className="text-sm text-slate-600 mt-1">{tab.description}</p>
                </div>
                <FontAwesomeIcon icon={faArrowRight} className="text-slate-400 mt-1" />
              </div>
            </button>
          ))}
        </section>

        <div className="flex gap-3 overflow-x-auto pb-2">
          {tabConfig.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full font-semibold whitespace-nowrap inline-flex items-center gap-2 border ${
                activeTab === tab.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200'
              }`}
            >
              <FontAwesomeIcon icon={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Staff Management Tab */}
        {activeTab === 'staff' && (
          <div className="space-y-6">
            <div className="rounded-2xl bg-blue-50 border border-blue-100 p-5">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-2xl bg-blue-600 text-white flex items-center justify-center shrink-0">
                  <FontAwesomeIcon icon={faPeopleGroup} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900">How to assign staff</h3>
                  <p className="text-sm text-slate-700 mt-1">
                    Change role first, then use the row buttons to manage teams or stations.
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto card">
              <table className="w-full">
                <thead className="border-b-2">
                  <tr>
                    <th className="text-left px-2 py-2 text-xs">ID</th>
                    <th className="text-left px-2 py-2 text-xs">Name</th>
                    <th className="text-left px-2 py-2 text-xs">Role</th>
                    <th className="text-left px-2 py-2 text-xs">Teams</th>
                    <th className="text-left px-2 py-2 text-xs">Stations</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {staff.map((member) => (
                    <tr key={member.id} className="border-b hover:bg-gray-50">
                      <td className="px-2 py-2 font-mono">{member.id}</td>
                      <td className="px-2 py-2">
                        <div className="font-semibold">{member.name}</div>
                        <div className="text-xs text-gray-500">({member.nickname})</div>
                      </td>
                      <td className="px-2 py-2">
                        <select
                          value={member.role}
                          onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                          className="border rounded px-2 py-1 text-xs"
                        >
                          <option value="staff">staff</option>
                          <option value="staff_station">staff_station</option>
                          <option value="admin">admin</option>
                        </select>
                      </td>
                      <td className="px-2 py-2">
                        <button
                          onClick={() => {
                            setEditingStaff(member.id)
                            setEditingMode('teams')
                          }}
                          className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-900 text-xs font-semibold px-3 py-2 rounded-full bg-blue-50 border border-blue-100"
                        >
                          <FontAwesomeIcon icon={faUsers} />
                          {member.teams.length > 0 ? `${member.teams.length} assigned` : 'Assign team'}
                        </button>
                      </td>
                      <td className="px-2 py-2">
                        <button
                          onClick={() => {
                            setEditingStaff(member.id)
                            setEditingMode('stations')
                          }}
                          className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-900 text-xs font-semibold px-3 py-2 rounded-full bg-blue-50 border border-blue-100"
                        >
                          <FontAwesomeIcon icon={faSchool} />
                          {member.stations.length > 0 ? `${member.stations.length} assigned` : 'Assign station'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Edit Modal */}
            {editingStaff && (
              <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm">
                <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto card bg-blue-50 border border-blue-100 shadow-2xl">
                  <div className="flex justify-between items-center mb-4 gap-4">
                    <h3 className="text-lg font-bold">
                      Edit staff assignment: {staff.find(s => s.id === editingStaff)?.name}
                    </h3>
                    <button onClick={() => setEditingStaff(null)} className="text-xl shrink-0" aria-label="Close assignment modal">✕</button>
                  </div>

                  <div className="flex gap-2 mb-4 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setEditingMode('teams')}
                      className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 ${editingMode === 'teams' ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200'}`}
                    >
                      <FontAwesomeIcon icon={faLayerGroup} /> Teams
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingMode('stations')}
                      className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 ${editingMode === 'stations' ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200'}`}
                    >
                      <FontAwesomeIcon icon={faToggleOn} /> Stations
                    </button>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Teams */}
                    <div className={editingMode === 'stations' ? 'opacity-60' : ''}>
                      <h4 className="font-semibold mb-2 inline-flex items-center gap-2"><FontAwesomeIcon icon={faUsers} /> Assign Teams</h4>
                      <div className="space-y-2">
                        {teams.map((team) => (
                          <label key={team.id} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={staff.find(s => s.id === editingStaff)?.teams.includes(team.id) || false}
                              onChange={() => handleToggleTeam(editingStaff, team.id)}
                              className="w-4 h-4"
                            />
                            <span>{team.emoji} {team.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Stations */}
                    <div className={editingMode === 'teams' ? 'opacity-60' : ''}>
                      <h4 className="font-semibold mb-2 inline-flex items-center gap-2"><FontAwesomeIcon icon={faSchool} /> Assign Stations</h4>
                      <div className="space-y-2">
                        {stations.map((station) => (
                          <label key={station.id} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={staff.find(s => s.id === editingStaff)?.stations.includes(station.id) || false}
                              onChange={() => handleToggleStation(editingStaff, station.id)}
                              className="w-4 h-4"
                            />
                            <span>{station.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stations Tab */}
        {activeTab === 'stations' && (
          <div className="space-y-6">
            <div className="rounded-2xl bg-amber-50 border border-amber-100 p-5">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-2xl bg-amber-600 text-white flex items-center justify-center shrink-0">
                  <FontAwesomeIcon icon={faCirclePlus} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900">Create new station</h3>
                  <p className="text-sm text-slate-700 mt-1">
                    Add the station here first, then assign it to staff in Staff Management.
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <h3 className="text-lg font-bold mb-4">Station creator</h3>
              <form onSubmit={handleCreateStation} className="flex gap-2 flex-col md:flex-row">
                <input
                  type="text"
                  value={newStationName}
                  onChange={(e) => setNewStationName(e.target.value)}
                  placeholder="Station name..."
                  className="input flex-1"
                />
                <button type="submit" className="btn btn-primary inline-flex items-center justify-center gap-2">
                  <FontAwesomeIcon icon={faCirclePlus} />
                  Add
                </button>
              </form>
            </div>

            <div className="card">
              <h3 className="text-lg font-bold mb-4 inline-flex items-center gap-2"><FontAwesomeIcon icon={faListCheck} /> All Stations ({stations.length})</h3>
              <div className="space-y-2">
                {stations.map((station) => (
                  <div key={station.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-xl border border-slate-200">
                    <span className="font-semibold inline-flex items-center gap-2"><FontAwesomeIcon icon={faSchool} />{station.name}</span>
                    <button
                      onClick={() => handleDeleteStation(station.id)}
                      className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 text-sm font-semibold"
                    >
                      <FontAwesomeIcon icon={faTrashCan} />
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Scores Tab */}
        {activeTab === 'scores' && (
          <div className="space-y-4">
            <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-5">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-2xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
                  <FontAwesomeIcon icon={faWandMagicSparkles} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900">Score add page</h3>
                  <p className="text-sm text-slate-700 mt-1">
                    Pick a team, select a score type, choose a station when needed, then submit.
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <h3 className="text-lg font-bold mb-6 inline-flex items-center gap-2"><FontAwesomeIcon icon={faChalkboardUser} /> Add Score (Admin)</h3>
            <ScoreForm
              teams={teams}
              stations={stations}
              userRole="admin"
              userStations={stations.map(s => s.id)}
              onSubmit={() => {
                setToast({ message: '✓ Score added', type: 'success' })
              }}
            />
            </div>
          </div>
        )}

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </div>
  )
}

export default Admin
